import { rem } from 'polished';
import * as React from 'react';
import styled, { css } from 'styled-components';
import { mediaQueryWidth } from '../../common/breakpoints';
import { darkGrey5, darkGrey6, white } from '../../common/colors';
import { useHeight } from '../../common/hooks/useHeight';
import { StyledPaddingContainer } from '../../common/padding.styles';
import { StyledInformationalPage } from '../../common/static.styles';
import { Button } from '../../components/button/Button';
import { FeaturedBanner } from '../../components/featured-banner/FeaturedBanner';
import { HeadTitle } from '../../components/head-title/HeadTitle';
import { ImageCards } from '../../components/image-cards/ImageCards';
import { RowHeader } from '../../components/row-header/RowHeader';
import { DefaultLayout } from '../../layout/default/DefaultLayout';

const CompanyInfoContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  height: 354px;

  ${mediaQueryWidth({ max: 650 })`
    height: 650px;
    flex-direction: column;
  `}
`;

const ServicesContainer = styled.section`
  padding: 10px 15px;
  display: flex;
  flex-direction: column;
  flex: 0.5;
  position: relative;
  background-color: ${darkGrey5};

  h2 {
    margin: 50px 0 30px 0;
    text-align: center;
    color: ${white};
    width: 100%;

    ${mediaQueryWidth({ min: 'desktop' })`
      font-size: ${rem(35)};
    `}

    ${mediaQueryWidth({ min: 'tablet', max: 'desktop' })`
      font-size: ${rem(28)};
    `}

    ${mediaQueryWidth({ max: 'tablet' })`
      font-size: ${rem(18)};
    `}
  }

  p {
    text-align: center;
    color: ${white};
    height: 145px;
  }

  a {
    text-align: center;
  }
`;

export interface CorporateRespContainerProps {
  height?: any;
}

const CorporateRespContainer = styled(ServicesContainer)<CorporateRespContainerProps>`
  background-color: ${darkGrey6};

  p {
    ${({ height }) =>
      height &&
      css`
        height: ${height}px;
      `}
  }
`;

const seo = {
  description: '',
};

const bannerTitle = {
  title: 'About the Studio',
  addlTitleData: {
    additionalLinks: [
      {
        displayText: 'Learn More',
        url: '/studio/about/company-overview',
      },
    ],
  },
};

const alt = 'WB About the Studio Panoramic shot';
const imageWithPresets: ImageWithPresets = {
  image: {
    alt,
    src: '/assets/company/company-banner-1920x480.jpg',
  },
  tabletImage: {
    alt,
    src: '/assets/company/company-banner-1920x480-tablet.jpg',
  },
  mobileImage: {
    alt,
    src: '/assets/company/company-banner-1920x480-mobile.jpg',
  },
};

const Company = () => {
  const [ref, height] = useHeight();

  return (
    <DefaultLayout locale='en-US' seo={seo} hideBottomPadding={true}>
      <HeadTitle title={bannerTitle.title} />
      <StyledInformationalPage>
        <FeaturedBanner headingTag='h1' imageWithPresets={imageWithPresets} titleProps={bannerTitle} />
        <section className='divisions'>
          <RowHeader headingTag='h2' title='Divisions' />
          <StyledPaddingContainer>
            <ImageCards
              cards={[
                {
                  alt: 'Divisions - Motion Pictures',
                  src: '/assets/company/divisions/motion-pictures.jpg',
                  title: 'Motion Pictures',
                  to: '/company/divisions/motion-pictures',
                },
                {
                  alt: 'Divisions - Games',
                  src: '/assets/company/divisions/games.jpg',
                  title: 'Games',
                  to: '/company/divisions/games',
                },
                {
                  alt: 'Divisions - Television',
                  src: '/assets/company/divisions/television.jpg',
                  title: 'Television',
                  to: '/company/divisions/television',
                },
                {
                  alt: 'Divisions - DC Entertainment',
                  src: '/assets/company/divisions/dc.jpg',
                  title: 'DC',
                  to: '/company/divisions/dc',
                },
                {
                  alt: 'Divisions - Themed Entertainment',
                  src: '/assets/company/divisions/themed-entertainment.jpg',
                  title: 'Themed Entertainment',
                  to: '/company/divisions/themed-entertainment',
                },
                {
                  alt: 'Divisions - Consumer Products',
                  src: '/assets/company/divisions/consumer-products.jpg',
                  title: 'Consumer Products',
                  to: '/company/divisions/consumer-products',
                },
                {
                  alt: 'Divisions - Animation',
                  src: '/assets/company/divisions/animation.jpg',
                  title: 'Animation',
                  to: '/company/divisions/animation',
                },
                {
                  alt: 'Divisions - Live Theatre',
                  src: '/assets/company/divisions/live-theatre.jpg',
                  title: 'Live Theatre',
                  to: '/company/divisions/live-theatre',
                },
                {
                  alt: 'Divisions - Studio Operations',
                  src: '/assets/company/divisions/studio-operations.jpg',
                  title: 'Studio Operations',
                  to: '/company/divisions/studio-operations',
                },
              ]}
              headingTag='h3'
              lazyLoad={true}
              ratio={0.8}
            />
          </StyledPaddingContainer>
        </section>
        <section className='leadership'>
          <RowHeader headingTag='h2' title='Leadership' />
          <StyledPaddingContainer>
            <ImageCards
              cards={[
                {
                  alt: 'Executive Leadership - Operating Officers',
                  src: '/assets/company/leadership/operating-officers.jpg',
                  title: 'Operating Officers',
                  to: '/company/leadership/operating-officers',
                },
                {
                  alt: 'Executive Leadership - Divisional Executives',
                  src: '/assets/company/leadership/divisional-executives.jpg',
                  title: 'Divisional Executives',
                  to: '/company/leadership/divisional-executives',
                },
              ]}
              headingTag='h3'
              lazyLoad={true}
              ratio={0.8}
            />
          </StyledPaddingContainer>
        </section>
        <CompanyInfoContainer>
          <ServicesContainer>
            <h2>WB Services</h2>
            <p ref={ref}>
              <strong>Warner Bros. Studios</strong> is one of the most memorable motion picture and television
              production facilities in the world. We offer many services to the community - From finding a job to
              renting a stage on the historic Warner Bros. lot
            </p>
            <Button aria-label='WB Services' href={'/studio/services'} invertColors={true}>
              Learn More
            </Button>
          </ServicesContainer>
          <CorporateRespContainer height={height}>
            <h2>Corporate Responsibility</h2>
            <p>
              At <strong>WarnerMedia</strong>, equity, diversity, inclusion, and corporate social, responsibility are
              moral and business imperatives. We see advocacy and inclusion as a key part to our success, and our
              approach to these critical issues are rooted in how we address our workforce, programs, content,
              suppliers, and community.
            </p>
            <Button
              aria-label='Corporate Responsibility'
              target='_blank'
              href={'https://www.warnermedia.com/us/story/we-shape-our-communities'}
              invertColors={true}
            >
              Learn More
            </Button>
          </CorporateRespContainer>
        </CompanyInfoContainer>
      </StyledInformationalPage>
    </DefaultLayout>
  );
};

export default Company;
